import * as React from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { routes } from '@app/routes';
import {
    BarsIcon,
    TimesIcon,
    HomeIcon,
    CubesIcon,
    MonitoringIcon,
    BuildingIcon,
    CreditCardIcon,
    ChartLineIcon,
    BoltIcon,
    OutlinedQuestionCircleIcon
} from '@patternfly/react-icons';
import AdVoltLogo from '../../../assets/images/AdVolt-logo-white-500px.png';
import { getSubscription, getUserInfo } from "@app/utils/utils";
import { useEffect, useState } from "react";
import { UsernameDropdown } from "@app/AppLayout/UsernameDropdown";
import { useWindowSize, isMobile, isTablet } from "@app/utils/useWindowSize";


interface IAppLayout {
    children: React.ReactNode;
}

// Map route labels to icons
const routeIcons: { [key: string]: React.ReactNode } = {
    'Dashboard': <HomeIcon />,
    'Ads': <CubesIcon />,
    'Campaigns': <MonitoringIcon />,
    'Organizations': <BuildingIcon />,
    'Plan': <CreditCardIcon />,
    'Analytics': <ChartLineIcon />,
    'Help': <OutlinedQuestionCircleIcon />
};

// Routes that authenticated users may visit without an active subscription.
// Anything else triggers a forced redirect once on mount: brand-new users (no
// real subscription yet) go to /organizations for the guided onboarding tour,
// lapsed/incomplete subscriptions go to /plan.
const SUB_FREE_PATHS = new Set<string>(['/plan', '/login', '/organizations', '/help']);

const AppLayout: React.FunctionComponent<IAppLayout> = ({ children }) => {
    const location = useLocation();
    const navigate = useNavigate();
    const [userInfo, setUserInfo] = useState<any>();
    const [isNavOpen, setIsNavOpen] = useState(true);
    const [hoveredNav, setHoveredNav] = useState<string | null>(null);
    // Onboarding tour step 2: highlight the Plan tab after the user registers
    // their first organization, until they visit the Plan page.
    const [highlightPlan, setHighlightPlan] = useState<boolean>(
        () => sessionStorage.getItem('advolt_highlight_plan') === 'true'
    );
    const { width } = useWindowSize();
    const mobile = isMobile(width);
    const tablet = isTablet(width);
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

    useEffect(() => {
        getUserInfo().then(info => {
            setUserInfo(info);
        });
    }, []);

    // Force users without an active subscription to /plan once per session.
    // (Layout only mounts on authenticated routes.)
    useEffect(() => {
        let cancelled = false;
        getSubscription()
            .then((sub) => {
                if (cancelled) return;
                const hasActive = !!sub && (sub.status === 'ACTIVE' || sub.status === 'TRIALING');
                if (!hasActive && !SUB_FREE_PATHS.has(location.pathname)) {
                    // Brand-new users start on the Organizations page for the guided
                    // onboarding tour; users with a lapsed/incomplete subscription go
                    // to /plan to sort out billing.
                    //
                    // A brand-new user does NOT come back as a null subscription — the
                    // backend returns a real SubscriptionResponse with tier === null and
                    // status === 'INCOMPLETE' (see subscription.ts). So detect "brand new"
                    // by the absence of a real tier, not by the absence of the record.
                    const brandNew = !sub || sub.tier == null;
                    navigate(brandNew ? '/organizations' : '/plan', { replace: true });
                }
            })
            .catch(() => {
                // Ignore — backend may be unreachable; don't trap the user.
            });
        return () => {
            cancelled = true;
        };
        // Intentionally only run once on mount — re-running on every path
        // change would loop while the user is mid-checkout etc.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Listen for the onboarding signal raised after first-org registration.
    // (AppLayout is already mounted, so a same-tab sessionStorage write alone
    // won't notify it — Organizations dispatches this event explicitly.)
    useEffect(() => {
        const onHighlightPlan = () => setHighlightPlan(true);
        window.addEventListener('advolt:highlight-plan', onHighlightPlan);
        return () => window.removeEventListener('advolt:highlight-plan', onHighlightPlan);
    }, []);

    // Once the user reaches the Plan page, the nudge has served its purpose.
    useEffect(() => {
        if (location.pathname === '/plan' || location.pathname.startsWith('/plan/')) {
            setHighlightPlan(false);
            sessionStorage.removeItem('advolt_highlight_plan');
        }
    }, [location.pathname]);

    // Close mobile menu on route change
    useEffect(() => {
        setMobileMenuOpen(false);
    }, [location.pathname]);

    // Auto-collapse sidebar on tablet
    useEffect(() => {
        if (tablet) {
            setIsNavOpen(false);
        } else if (!mobile) {
            setIsNavOpen(true);
        }
    }, [tablet, mobile]);

    const displayName = userInfo == null ? "" : userInfo.displayName;

    const onNavToggle = () => {
        if (mobile) {
            setMobileMenuOpen(!mobileMenuOpen);
        } else {
            setIsNavOpen(!isNavOpen);
        }
    };

    const sidebarWidth = isNavOpen ? 240 : 76;
    const showSidebar = mobile ? mobileMenuOpen : true;

    const styles = {
        layout: {
            display: 'flex',
            minHeight: '100vh',
            height: '100vh',
            overflow: 'hidden',
            position: 'relative' as const
        },
        sidebarOverlay: {
            position: 'fixed' as const,
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            zIndex: 99,
            display: mobile && mobileMenuOpen ? 'block' : 'none'
        },
        sidebar: {
            width: mobile ? '280px' : sidebarWidth + 'px',
            minWidth: mobile ? '280px' : sidebarWidth + 'px',
            background: 'linear-gradient(180deg, #1a1a2e 0%, #16213e 100%)',
            display: 'flex',
            flexDirection: 'column' as const,
            transition: 'all 0.3s ease',
            position: mobile ? 'fixed' as const : 'relative' as const,
            top: mobile ? 0 : undefined,
            left: mobile ? (mobileMenuOpen ? '0' : '-280px') : undefined,
            bottom: mobile ? 0 : undefined,
            zIndex: 100,
            boxShadow: mobile && mobileMenuOpen ? '4px 0 20px rgba(0, 0, 0, 0.3)' : '4px 0 20px rgba(0, 0, 0, 0.1)',
            flexShrink: 0,
            height: mobile ? '100vh' : undefined
        },
        logoSection: {
            padding: isNavOpen ? '20px 20px' : '20px 16px',
            borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: isNavOpen ? 'flex-start' : 'center',
            gap: '12px',
            minHeight: '70px'
        },
        logoIcon: {
            width: '36px',
            height: '36px',
            minWidth: '36px',
            borderRadius: '10px',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#fbbf24',
            fontSize: '18px',
            fontWeight: 800,
            flexShrink: 0,
            boxShadow: '0 4px 12px rgba(102, 126, 234, 0.4)'
        },
        logoText: {
            color: '#ffffff',
            fontSize: '20px',
            fontWeight: 700,
            letterSpacing: '-0.5px',
            opacity: isNavOpen ? 1 : 0,
            width: isNavOpen ? 'auto' : 0,
            overflow: 'hidden',
            transition: 'opacity 0.2s ease, width 0.3s ease',
            whiteSpace: 'nowrap' as const
        },
        navSection: {
            flex: 1,
            padding: isNavOpen ? '20px 12px' : '20px 8px',
            overflowY: 'auto' as const,
            overflowX: 'hidden' as const
        },
        navLabel: {
            color: 'rgba(255, 255, 255, 0.4)',
            fontSize: '11px',
            fontWeight: 600,
            textTransform: 'uppercase' as const,
            letterSpacing: '1px',
            padding: '0 8px',
            marginBottom: '12px',
            opacity: isNavOpen ? 1 : 0,
            height: isNavOpen ? 'auto' : 0,
            overflow: 'hidden',
            transition: 'opacity 0.2s ease'
        },
        navList: {
            listStyle: 'none',
            margin: 0,
            padding: 0,
            display: 'flex',
            flexDirection: 'column' as const,
            gap: '4px'
        },
        navItem: {
            borderRadius: '12px',
            overflow: 'visible'
        },
        navLink: {
            display: 'flex',
            alignItems: 'center',
            gap: '14px',
            padding: isNavOpen ? '14px 16px' : '14px 0',
            color: 'rgba(255, 255, 255, 0.7)',
            textDecoration: 'none',
            borderRadius: '12px',
            transition: 'all 0.2s ease',
            justifyContent: isNavOpen ? 'flex-start' : 'center',
            position: 'relative' as const,
            width: '100%'
        },
        navLinkHover: {
            background: 'rgba(255, 255, 255, 0.08)',
            color: '#ffffff'
        },
        navLinkActive: {
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: '#ffffff',
            boxShadow: '0 4px 15px rgba(102, 126, 234, 0.4)'
        },
        navIcon: {
            fontSize: '20px',
            width: '24px',
            height: '24px',
            minWidth: '24px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0
        },
        navText: {
            fontSize: '14px',
            fontWeight: 500,
            opacity: isNavOpen ? 1 : 0,
            width: isNavOpen ? 'auto' : 0,
            overflow: 'hidden',
            transition: 'opacity 0.2s ease, width 0.2s ease',
            whiteSpace: 'nowrap' as const,
            display: isNavOpen ? 'block' : 'none'
        },
        mainArea: {
            flex: 1,
            display: 'flex',
            flexDirection: 'column' as const,
            overflow: 'hidden',
            background: '#f8fafc'
        },
        header: {
            background: '#ffffff',
            height: mobile ? '56px' : '70px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: mobile ? '0 12px' : '0 24px',
            borderBottom: '1px solid #e5e7eb',
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)'
        },
        headerLeft: {
            display: 'flex',
            alignItems: 'center',
            gap: '16px'
        },
        menuButton: {
            width: '40px',
            height: '40px',
            borderRadius: '10px',
            border: 'none',
            background: '#f3f4f6',
            color: '#4b5563',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'all 0.2s ease',
            fontSize: '18px'
        },
        headerTitle: {
            fontSize: '18px',
            fontWeight: 600,
            color: '#1f2937'
        },
        headerRight: {
            display: 'flex',
            alignItems: 'center',
            gap: '16px'
        },
        content: {
            flex: 1,
            overflow: 'auto'
        },
        sidebarFooter: {
            padding: isNavOpen ? '16px' : '16px 8px',
            borderTop: '1px solid rgba(255, 255, 255, 0.1)',
            display: isNavOpen ? 'block' : 'none'
        },
        versionBadge: {
            background: 'rgba(102, 126, 234, 0.2)',
            color: '#a5b4fc',
            padding: '6px 12px',
            borderRadius: '20px',
            fontSize: '11px',
            fontWeight: 600,
            textAlign: 'center' as const
        },
        tooltip: {
            position: 'absolute' as const,
            left: '100%',
            marginLeft: '10px',
            background: '#1f2937',
            color: '#ffffff',
            padding: '8px 12px',
            borderRadius: '8px',
            fontSize: '13px',
            fontWeight: 500,
            whiteSpace: 'nowrap' as const,
            zIndex: 1000,
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)',
            opacity: 0,
            pointerEvents: 'none' as const,
            transition: 'opacity 0.2s ease'
        },
        tooltipVisible: {
            opacity: 1
        },
        // Onboarding step-2 highlight on the Plan nav item.
        planHintLink: {
            background: 'linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%)',
            color: '#1f2937',
            animation: 'planHintPulse 1.8s ease-in-out infinite'
        },
        planHintBadge: {
            marginLeft: 'auto',
            background: '#ffffff',
            color: '#b45309',
            fontSize: '11px',
            fontWeight: 700,
            padding: '2px 8px',
            borderRadius: '10px',
            whiteSpace: 'nowrap' as const,
            boxShadow: '0 2px 6px rgba(0, 0, 0, 0.15)',
            flexShrink: 0
        }
    };

    // Get current page title - check exact match first, then find closest parent route
    const currentRoute = routes.find(r => r.path === location.pathname)
        || routes.find(r => r.label && r.path !== '/' && location.pathname.startsWith(r.path));
    const pageTitle = (currentRoute && currentRoute.label) ? currentRoute.label : 'Dashboard';

    return (
        <div style={styles.layout}>
            {/* Guided Tour Sidebar Overlay Styles */}
            <style>{`
                .guided-tour-active .sidebar-nav {
                    pointer-events: none;
                }
                .guided-tour-active .sidebar-overlay {
                    opacity: 1;
                    visibility: visible;
                }
                @keyframes planHintPulse {
                    0%, 100% { box-shadow: 0 0 0 2px rgba(251, 191, 36, 0.5); }
                    50% { box-shadow: 0 0 0 6px rgba(251, 191, 36, 0.25); }
                }
            `}</style>

            {/* Mobile Sidebar Overlay */}
            <div style={styles.sidebarOverlay} onClick={() => setMobileMenuOpen(false)} />

            {/* Sidebar */}
            <aside style={styles.sidebar} className="sidebar-nav">
                {/* Logo */}
                <div style={styles.logoSection}>
                    <div style={styles.logoIcon}><BoltIcon className="logo-bolt-pulse" /></div>
                    <span style={styles.logoText}>AdVolt</span>
                </div>

                {/* Navigation */}
                <nav style={styles.navSection}>
                    <div style={styles.navLabel}>Menu</div>
                    <ul style={styles.navList}>
                        {routes.map((route, idx) => {
                            if (!route.label) return null;
                            const isActive = route.path === '/'
                                ? location.pathname === '/'
                                : location.pathname === route.path || location.pathname.startsWith(route.path + '/');
                            const isHovered = hoveredNav === route.label;
                            const icon = routeIcons[route.label] || <CubesIcon />;
                            const showPlanHint = highlightPlan && route.label === 'Plan';

                            return (
                                <li key={`${route.label}-${idx}`} style={styles.navItem}>
                                    <NavLink
                                        end
                                        to={route.path}
                                        style={{
                                            ...styles.navLink,
                                            ...(isActive ? styles.navLinkActive : {}),
                                            ...(!isActive && isHovered ? styles.navLinkHover : {}),
                                            ...(showPlanHint ? styles.planHintLink : {})
                                        }}
                                        onMouseEnter={() => setHoveredNav(route.label || null)}
                                        onMouseLeave={() => setHoveredNav(null)}
                                    >
                                        <span style={styles.navIcon}>{icon}</span>
                                        <span style={styles.navText}>{route.label}</span>

                                        {/* Onboarding nudge: "Start here" badge on the Plan tab */}
                                        {showPlanHint && isNavOpen && (
                                            <span style={styles.planHintBadge}>Start here</span>
                                        )}

                                        {/* Tooltip for collapsed state */}
                                        {!isNavOpen && (
                                            <span style={{
                                                ...styles.tooltip,
                                                ...(isHovered ? styles.tooltipVisible : {})
                                            }}>
                                                {route.label}
                                            </span>
                                        )}
                                    </NavLink>
                                </li>
                            );
                        })}
                    </ul>
                </nav>

                {/* Footer */}
                <div style={styles.sidebarFooter}>
                    <div style={styles.versionBadge}>v{process.env.APP_VERSION}</div>
                </div>

                {/* Guided Tour Overlay */}
                <div
                    className="sidebar-overlay"
                    style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: 'rgba(0, 0, 0, 0.5)',
                        backdropFilter: 'blur(2px)',
                        opacity: 0,
                        visibility: 'hidden',
                        transition: 'opacity 0.3s ease, visibility 0.3s ease',
                        zIndex: 50,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                    }}
                >
                    <div style={{
                        background: 'rgba(255, 255, 255, 0.1)',
                        backdropFilter: 'blur(4px)',
                        padding: '12px 20px',
                        borderRadius: '12px',
                        color: 'rgba(255, 255, 255, 0.9)',
                        fontSize: '13px',
                        fontWeight: 500,
                        textAlign: 'center',
                        border: '1px solid rgba(255, 255, 255, 0.2)'
                    }}>
                        Complete the setup first
                    </div>
                </div>
            </aside>

            {/* Main Area */}
            <div style={styles.mainArea}>
                {/* Header */}
                <header style={styles.header}>
                    <div style={styles.headerLeft}>
                        <button
                            style={styles.menuButton}
                            onClick={onNavToggle}
                            aria-label="Toggle navigation"
                        >
                            {isNavOpen ? <TimesIcon /> : <BarsIcon />}
                        </button>
                        <span style={styles.headerTitle}>{pageTitle}</span>
                    </div>
                    <div style={styles.headerRight}>
                        <UsernameDropdown username={displayName} />
                    </div>
                </header>

                {/* Content */}
                <main style={styles.content} id="primary-app-container">
                    {children}
                </main>
            </div>
        </div>
    );
}

export { AppLayout };
